import React, { useEffect, useMemo, useRef } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { Alert, Box, Button, CircularProgress, Stack } from "@mui/material";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import BaseDialog from "../../utils/dialog/BaseDialog";
import { useScheduleEditor } from "./hooks/useScheduleEditor";
import ExistingScheduleStep from "./components_legacy/ExistingScheduleStep";
import NewScheduleForm from "./components_legacy/NewScheduleForm";
import RowInfoPanel from "./components_legacy/RowInfoPanel";
import styles from "./ScheduleTaggingDialogV2.module.css";

const queryClientConfig = {
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
};

export default function ScheduleTaggingDialogV2(props) {
  const queryClient = useMemo(() => new QueryClient(queryClientConfig), []);

  return (
    <QueryClientProvider client={queryClient}>
      <ScheduleTaggingDialogV2Content {...props} />
    </QueryClientProvider>
  );
}

function ScheduleTaggingDialogV2Content({
  open,
  editMode,
  onClose,
  row,
  section,
  error = null,
  companyType = "common",
  ...editorProps
}) {
  const autoSubmitKeyRef = useRef(null);
  const editor = useScheduleEditor({
    open,
    editMode,
    onClose,
    row,
    section,
    companyType,
    ...editorProps,
  });

  const {
    activeTab,
    existingScheduleStep,
    selectedScheduleType,
    selectedSchedule,
    initialTargetSchedule,
    schedules,
    recommendedSchedule,
    autoMatchedSchedule,
    isRowTypeDisabled,
    autoTaggingModeOn,
    rowTypeRequired,
    isSameSchedule,
    isInitialLoading,
    isBusy,
    setActiveTab,
    setExistingScheduleStep,
    setSelectedSchedule,
    confirmExistingSchedule,
    createNewSchedule,
  } = editor;

  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
    register,
    reset,
    trigger,
    watch,
  } = useForm({
    mode: "onChange",
    defaultValues: initialTargetSchedule,
  });

  useEffect(() => {
    reset(initialTargetSchedule);
    void trigger();
  }, [initialTargetSchedule, reset, trigger]);

  const shouldAutoConfirmBinding = Boolean(
    autoTaggingModeOn &&
    autoMatchedSchedule &&
    isAllocationExpenseAutoBinding(row)
  );

  useEffect(() => {
    if (!open) {
      autoSubmitKeyRef.current = null;
      return;
    }

    if (!shouldAutoConfirmBinding || isBusy) return;

    const autoSubmitKey = `${row?.row_id || ""}:${autoMatchedSchedule.sch_id}`;
    if (autoSubmitKeyRef.current === autoSubmitKey) return;

    autoSubmitKeyRef.current = autoSubmitKey;
    confirmExistingSchedule(initialTargetSchedule);
  }, [
    open,
    shouldAutoConfirmBinding,
    autoMatchedSchedule,
    row,
    isBusy,
    initialTargetSchedule,
    confirmExistingSchedule,
  ]);

  const rowType = watch("rowType");
  const canSubmitExisting =
    Boolean(autoMatchedSchedule || selectedSchedule.sch_id !== null) &&
    (!rowTypeRequired || existingScheduleStep === 0 || Boolean(rowType)) &&
    !isSameSchedule &&
    !isBusy;

  const existingButtonText = getExistingButtonText({
    autoMatchedSchedule,
    existingScheduleStep,
    rowTypeRequired,
    editMode,
    isSameSchedule,
  });

  const showActions =
    selectedScheduleType && !isInitialLoading && !shouldAutoConfirmBinding;

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      closeDisabled={isBusy}
      maxWidth='md'
      title={editMode ? "Adjust Tax Settings" : "Assign to Schedule"}
      subtitle='Review the row and choose how it should be bound.'
      icon={<LocalOfferIcon />}
      actions={
        showActions ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Button onClick={onClose} variant='outlined' disabled={isBusy}>
              Cancel
            </Button>

            <Stack direction='row' spacing={1.5}>
              {activeTab === 0 || autoMatchedSchedule ? (
                <>
                  {existingScheduleStep === 1 && !autoMatchedSchedule && (
                    <Button
                      onClick={() => setExistingScheduleStep(0)}
                      variant='outlined'
                      disabled={isBusy}
                    >
                      Back
                    </Button>
                  )}

                  <Button
                    onClick={handleSubmit(confirmExistingSchedule)}
                    variant='contained'
                    disabled={!canSubmitExisting}
                    startIcon={
                      isBusy ? (
                        <CircularProgress size={18} color='inherit' />
                      ) : null
                    }
                  >
                    {existingButtonText}
                  </Button>
                </>
              ) : (
                <Button
                  onClick={handleSubmit(createNewSchedule)}
                  variant='contained'
                  disabled={!isValid || isBusy}
                  startIcon={
                    isBusy ? (
                      <CircularProgress size={18} color='inherit' />
                    ) : null
                  }
                >
                  {editMode ? "Create and Update" : "Create Schedule"}
                </Button>
              )}
            </Stack>
          </Box>
        ) : null
      }
    >
      <Stack spacing={2.5}>
        {error && <Alert severity='error'>{error}</Alert>}

        <RowInfoPanel row={row} section={section} editMode={editMode} />

        {isInitialLoading ? (
          <ScheduleEditorSkeleton />
        ) : (
          selectedScheduleType && (
            <div className={styles.linearFlow}>
              {autoTaggingModeOn && autoMatchedSchedule ? (
                <AutoMatchedBanner schedule={autoMatchedSchedule} />
              ) : (
                <>
                  <div className={styles.modeTabs}>
                    <button
                      type='button'
                      className={`${styles.modeButton} ${
                        activeTab === 0 ? styles.modeButtonActive : ""
                      }`}
                      onClick={() => setActiveTab(0)}
                    >
                      Existing schedules ({schedules.length})
                    </button>
                    <button
                      type='button'
                      className={`${styles.modeButton} ${
                        activeTab === 1 ? styles.modeButtonActive : ""
                      }`}
                      onClick={() => setActiveTab(1)}
                    >
                      Create new
                    </button>
                  </div>

                  {activeTab === 0 ? (
                    <ExistingScheduleStep
                      schedules={schedules}
                      selectedScheduleType={selectedScheduleType}
                      selectedSchedule={selectedSchedule}
                      setSelectedSchedule={setSelectedSchedule}
                      existingScheduleStep={existingScheduleStep}
                      control={control}
                      errors={errors}
                      companyType={companyType}
                      isRowTypeDisabled={isRowTypeDisabled}
                      rowTypeRequired={rowTypeRequired}
                      editMode={editMode}
                      row={row}
                      recommendedSchedule={recommendedSchedule}
                    />
                  ) : (
                    <NewScheduleForm
                      selectedScheduleType={selectedScheduleType}
                      selectedSchedule={selectedSchedule}
                      register={register}
                      control={control}
                      errors={errors}
                      companyType={companyType}
                      isRowTypeDisabled={isRowTypeDisabled}
                      rowTypeRequired={rowTypeRequired}
                      editMode={editMode}
                      row={row}
                    />
                  )}
                </>
              )}
            </div>
          )
        )}
      </Stack>
    </BaseDialog>
  );
}

function AutoMatchedBanner({ schedule }) {
  return (
    <div className={styles.successBanner}>
      <div>
        <p className={styles.bannerTitle}>
          Automatically matched {schedule.scheduleTitle}
        </p>
        <p className={styles.bannerText}>
          The row can be bound directly to Schedule No. {schedule.scheduleNo}.
        </p>
      </div>
      <span className={styles.recommendedBadge}>Ready</span>
    </div>
  );
}

function ScheduleEditorSkeleton() {
  return (
    <div className={styles.skeletonStack} aria-label='Loading schedules'>
      <div className={styles.skeletonCard} />
      <div className={styles.skeletonLine} />
      <div className={`${styles.skeletonLine} ${styles.skeletonLineShort}`} />
    </div>
  );
}

function getExistingButtonText({
  autoMatchedSchedule,
  existingScheduleStep,
  rowTypeRequired,
  editMode,
  isSameSchedule,
}) {
  if (autoMatchedSchedule) return "Confirm Binding";
  if (existingScheduleStep === 0 && rowTypeRequired) return "Next";
  if (isSameSchedule) return "No Changes";
  return editMode ? "Update Schedule" : "Select Schedule";
}

function isAllocationExpenseAutoBinding(row) {
  return [
    "Allocation of Expenses/Common Expenses",
    "Allocation of Expenses/Statutory Expenses",
  ].includes(row?.taxAdjustment);
}
